import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';
const compareObjects = require('fast-deep-equal');

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import { IValuesCollection } from '../ts/appTheme';
import { ProgressBar } from './ProgressBar';
import * as Globalization from '../../../_common/platform/ts/Globalization';
import { IDisplayValue, IWidth } from '../ts/Interfaces';
import { IGetValuesResult as IEntityModelGetValuesResult } from '../../../pdc-platform/src/ts/stores/EntityModelsStore';
import { themeVars } from '../../../pdc-platform/src/ts/themes/themeConstants';
import { getCSSWidth } from '../ts/Utilities';
import { ISuggestionValue, ListSelectItem, ValidState } from './selectors/ListSelectItem';

import {
    CLEAR_SELECTIONS_VALUE,
    IMatch,
    TitleOfMatch,
    SearchField,
    NoItemElement,
    getSearchHighlightingColor,
    SHOW_MORE_VALUE,
    ShowMoreElement
} from './selectors/Buttons';

export { ISuggestionValue } from './selectors/ListSelectItem';

export function areSuggestionValuesEqual(value1: ISuggestionValue, value2: ISuggestionValue) {
    if (value1 != null && value2 != null) {
        return (
            value1.value === value2.value &&
            (value1.type == null || value2.type == null || value1.type === value2.type) &&
            (value1.displayValue == null || value2.displayValue == null || value1.displayValue === value2.displayValue)
        );
    }

    return false;
}

export interface ISearchResult {
    value: ISuggestionValue;
    title?: string;
    match?: IMatch;
    matchStartIndex?: number;
}

export interface IGetValuesResult extends IEntityModelGetValuesResult {
    validValues?: ISuggestionValue[];
}

export type GetValuesResult = JQuery.Deferred<IGetValuesResult>;

export interface IType {
    key: string;
    title: string;
}

export type IGetDisplayValue = (title: string) => string;
export type IUpdateStateHook = (updateState: (values: ISuggestionValue[]) => void) => void;

export interface ISuggestionListFieldProperties {
    value?: ISuggestionValue[];
    singleSelectMode?: boolean;
    displayValues?: ISuggestionValue[];
    validValues?: ISuggestionValue[];
    keySelect?: boolean;
    allowNull?: boolean;
    sortByDisplayValue?: boolean;
    allowAdding?: boolean;
    readOnly?: boolean;
    types?: IType[]; // 'types' are an array to control the order of appearance.
    listThreshold?: number;
    selectFirstItemByDefault?: boolean;
    defaultSearchString?: string;
    theme: IValuesCollection;
    doNotSetFocusOnShowing?: boolean;
    popupMode?: boolean;
    width?: IWidth;
    minWidth?: IWidth;
    maxHeight?: IWidth;
    hideSelectAllAndClear?: boolean;
    hideSearchField?: boolean;
    glob?: IGlob;
    getValues?(): GetValuesResult;
    updateStateHook?: IUpdateStateHook;
    getDisplayValue?: IGetDisplayValue; // Is used to make custom formatting of displayed value.
    getIcon?(value: number): string;
    onChange(value: ISuggestionValue[]): void;
    onClose?(value: ISuggestionValue[]): void;
    onContainerClick?(e): void;
}

const ALL_ITEM_VALUE = 'all-item';
const SUGGESTIONS_LIST_THRESHOLD = 200;

// Input values can be wrong.
function normalizeValue(values: ISuggestionValue[]) {
    let result: ISuggestionValue[] = null;

    if (Array.isArray(values) && values.length > 0) {
        result = [];

        values.forEach((value) => {
            if (value != null) {
                result.push(value);
            }
        });

        if (result.length === 0) {
            result = null;
        }
    }

    return result;
}

function getItemKey(value: ISuggestionValue) {
    return 'item_' + value.value + (value.displayValue != null ? '_' + value.displayValue : '');
}

function searchForMatches(
    searchString: string,
    displayValues: ISuggestionValue[],
    type: string,
    keySelect: boolean,
    getDisplayValue: IGetDisplayValue,
    sortByDisplayValue: boolean,
    fromStartOnly: boolean
): ISearchResult[] {
    if (displayValues == null) {
        return [];
    }

    const result: ISearchResult[] = [];

    displayValues.forEach((value) => {
        let displayValue = value;

        if (type != null && value.type !== type) {
            return;
        }

        let title: string;

        if (keySelect) {
            title = displayValue.displayValue || displayValue.value;
        } else {
            title = displayValue.value;
        }

        if (getDisplayValue != null) {
            title = getDisplayValue(title);
        }

        if (title != null && title !== '') {
            const searchLength = searchString.length;

            if (typeof title !== 'string') {
                title = (title as any).toString();
            }

            // For empty search all values are right.
            const valueIndex = searchLength > 0 ? title.toLowerCase().indexOf(searchString) : 0;

            if (valueIndex === 0 || (!fromStartOnly && valueIndex > 0)) {
                result.push({
                    value: displayValue,
                    title: title,
                    matchStartIndex: valueIndex,
                    match: {
                        begining: valueIndex > 0 ? title.slice(0, valueIndex) : undefined,
                        exactMatch:
                            searchLength > 0 && valueIndex >= 0
                                ? title.slice(valueIndex, valueIndex + searchLength)
                                : undefined,
                        ending: title.slice(valueIndex + searchLength)
                    }
                });
            }
        }
    });

    if (sortByDisplayValue) {
        result.sort((a, b) => {
            if (a.title === b.title) {
                return 0;
            }

            if (a.title > b.title) {
                return 1;
            }

            if (a.title < b.title) {
                return -1;
            }
        });
    } else {
        result.sort((a, b) => {
            if (a.matchStartIndex === b.matchStartIndex) {
                return 0;
            }

            if (a.matchStartIndex > b.matchStartIndex) {
                return 1;
            }

            if (a.matchStartIndex < b.matchStartIndex) {
                return -1;
            }
        });
    }

    return result;
}

function checkValues(displayValues: IDisplayValue[], glob: IGlob) {
    const counters: { [value: string]: { count: number; displayValues: string } } = {};

    displayValues.forEach(({ value, displayValue }) => {
        let counter = counters[value];

        if (counter == null) {
            counters[value] = { count: 1, displayValues: displayValue ?? value };
        } else {
            counters[value] = {
                count: counter.count + 1,
                displayValues: counter.displayValues + ', ' + (displayValue ?? value)
            };
        }
    });

    let errorString = '';

    for (let value in counters) {
        if (counters[value].count > 1) {
            errorString += `\n Value '${value}' corresponds to ${counters[value].displayValues}.`;
        }
    }

    if (errorString.length > 0) {
        if (glob != null) {
            glob.AppActions.showNotification_Warning('Values must be unique to be selected properly', null, {
                manualDestroy: true,
                details: errorString
            });
        } else {
            console.log('Values must be unique to be selected properly\n' + errorString);
        }
    }
}

function isItemChecked(value: ISuggestionValue, values: ISuggestionValue[]) {
    if (values == null) {
        return false;
    }

    const item = values.find((selectedValue) => {
        return areSuggestionValuesEqual(selectedValue, value);
    });

    return item != null;
}

type IGetMatchByIndex = (matches: ISearchResult[], index: number) => ISuggestionValue;

const getPreviousMatch: IGetMatchByIndex = (match, i) => {
    return match[Math.max(0, i - 1)].value;
};

const getNextMatch: IGetMatchByIndex = (match, i) => {
    return match[Math.min(match.length - 1, i + 1)].value;
};

//EVG: This is useful when a Workspace has several SuggestionListFields shown as lists.
// It helps identify the current SuggestionListField to set focus properly.
// When SuggestionListField is shown in Drop Down, it is not needed, becuase there always can be only one Drop Down at time.
let _lastRuntimeID = 0;
let _currentRuntimeID = null;

export default function SuggestionListField(props: ISuggestionListFieldProperties) {
    const containerRef = React.useRef<HTMLUListElement>(null);
    const allItemRef = React.useRef<HTMLLIElement>(null);
    const clearSelectionsRef = React.useRef<HTMLLIElement>(null);
    const showMoreRef = React.useRef<HTMLLIElement>(null);
    const itemsRef = React.useRef<{ [value: string]: HTMLLIElement }>({});

    const searchFieldRef = React.useRef<HTMLInputElement>(null);
    const searchFieldIsFocused = React.useRef<boolean>(false);
    const [searchString, setSearchString] = React.useState(props.defaultSearchString || '');
    const searchStringIsChanged = React.useRef(false);

    const [focusedItem, setFocusedItem] = React.useState<ISuggestionValue>(undefined);

    const getValuesDef = React.useRef<GetValuesResult>(null);
    const getValuesResult = React.useRef<IGetValuesResult>({
        displayValues: props.displayValues != null ? [...props.displayValues] : null,
        validValues: props.validValues != null ? [...props.validValues] : null
    });
    const loading = React.useRef<boolean>(typeof props.getValues === 'function');
    const shownItems = React.useRef<ISearchResult[]>(null);

    const [runtimeID] = React.useState(++_lastRuntimeID);
    const [, setRefreshKey] = React.useState(0);
    const [listThreshold, setListThreshold] = React.useState(
        props.listThreshold > 0 ? props.listThreshold : SUGGESTIONS_LIST_THRESHOLD
    );
    const [values, setValues] = React.useState(normalizeValue(props.value));

    const [currentValueType, setCurrentValueType] = React.useState(() => {
        const { types } = props;

        if (Array.isArray(types) && types.length > 0) {
            let firstValue = values != null ? values[0] : null;

            if (firstValue != null) {
                firstValue = props.displayValues?.find((displayValue) => {
                    return displayValue.value === firstValue.value;
                });
            }

            if (firstValue != null && firstValue.type != null) {
                return firstValue.type;
            } else {
                return types[0].key;
            }
        }

        return undefined;
    });

    const dropGetValuesDef = React.useCallback(() => {
        if (getValuesDef.current != null && getValuesDef.current.reject != null) {
            getValuesDef.current.reject(true);
        }

        getValuesDef.current = undefined;
    }, []);

    React.useEffect(() => {
        if (typeof props.updateStateHook === 'function') {
            const updateState = (values: ISuggestionValue[]) => {
                setValues(values);
            };

            props.updateStateHook(updateState);
        }

        if (typeof props.getValues === 'function') {
            loading.current = true;

            const def = props.getValues();

            if (def == null) {
                throw new Error('props.getValues must return a Deferred value');
            }

            getValuesDef.current = def;

            def.done((result) => {
                if (loading.current) {
                    if (!compareObjects(result, getValuesResult.current)) {
                        getValuesResult.current = $.extend(true, {}, result);
                    }

                    const displayValues = getValuesResult.current?.displayValues;

                    if (displayValues?.length > 0) {
                        checkValues(displayValues, props.glob);

                        if (props.selectFirstItemByDefault === true && values == null && searchString === '') {
                            if (displayValues.length === 1) {
                                const validValues = getValuesResult.current.validValues;

                                if (validValues == null || validValues.length === 0) {
                                    const validValue = displayValues[0];

                                    getValuesResult.current.validValues = [validValue];
                                    processItemClick(validValue, false);
                                }
                            }
                        }
                    }

                    loading.current = false;
                    setRefreshKey((refreshKey) => ++refreshKey);
                }
            }).fail((cancel) => {
                if (cancel) {
                    loading.current = false;
                    return;
                }

                if (loading.current) {
                    loading.current = false;
                    setRefreshKey((refreshKey) => ++refreshKey);
                }
            });
        } else {
            getValuesResult.current = {
                displayValues: props.displayValues
            };
        }

        return () => {
            dropGetValuesDef();
        };
    }, [
        props.updateStateHook,
        props.getValues,
        props.singleSelectMode,
        JSON.stringify(props.displayValues),
        JSON.stringify(props.validValues),
        props.keySelect,
        props.allowNull,
        props.sortByDisplayValue,
        props.allowAdding,
        props.types,
        props.selectFirstItemByDefault,
        props.defaultSearchString
    ]);

    React.useEffect(() => {
        const newListThreshold = props.listThreshold > 0 ? props.listThreshold : SUGGESTIONS_LIST_THRESHOLD;

        if (newListThreshold !== listThreshold) {
            setListThreshold(props.listThreshold);
        }
    }, [props.listThreshold]);

    React.useEffect(() => {
        const value = normalizeValue(props.value);

        setValues(value);
    }, [JSON.stringify(props.value)]);

    React.useEffect(() => {
        if (props.doNotSetFocusOnShowing && _currentRuntimeID == null) {
            return;
        }

        if (props.popupMode) {
            _currentRuntimeID = runtimeID;
        } else {
            if (_currentRuntimeID == null) {
                _currentRuntimeID = runtimeID;
            } else if (_currentRuntimeID !== runtimeID) {
                return;
            }
        }

        if (focusedItem != null) {
            let ref: HTMLElement;

            if (focusedItem.value === ALL_ITEM_VALUE) {
                ref = allItemRef.current;
            } else if (focusedItem.value === CLEAR_SELECTIONS_VALUE) {
                ref = clearSelectionsRef.current;
            } else if (focusedItem.value === SHOW_MORE_VALUE) {
                ref = showMoreRef.current;

                if (ref == null && shownItems.current.length > 0) {
                    setFocusedItem(shownItems.current[shownItems.current.length - 1].value);
                    return;
                }
            } else {
                ref = itemsRef.current[getItemKey(focusedItem)];
            }

            if (ref != null) {
                ref.focus();
                return;
            }
        }

        if (searchFieldRef.current == null) {
            containerRef.current.focus();
        } else if (focusedItem === null) {
            // Null means to set focus to Search Field.
            const searchField = searchFieldRef.current;

            if (searchField != null) {
                searchField.focus();
            }
        }
    });

    React.useEffect(() => {
        return () => {
            if (_currentRuntimeID === runtimeID) {
                _currentRuntimeID = null;
            }
        };
    }, []);

    const handleOnFocus = React.useCallback(() => {
        _currentRuntimeID = runtimeID;
    }, []);

    const handleOnBlur = React.useCallback(() => {
        if (props.doNotSetFocusOnShowing) {
            _currentRuntimeID = null;
        }
    }, [props.doNotSetFocusOnShowing]);

    const getMatches = React.useCallback(
        (searchString: string): ISearchResult[] => {
            const displayValues = getValuesResult.current.displayValues;

            let result: ISearchResult[];

            if (searchString !== '') {
                const loweredSearchString = searchString.toLowerCase(); //searchString.replace(/^\s+|\s+$/g, '').toLowerCase();
                const searchFromStartOnly = props.theme?.[themeVars.itemSelector_SearchFromStartOnly];
                const matches = searchForMatches(
                    loweredSearchString,
                    displayValues,
                    currentValueType,
                    props.keySelect,
                    props.getDisplayValue,
                    props.sortByDisplayValue,
                    searchFromStartOnly
                );

                if (result == null) {
                    result = matches;
                } else {
                    result.push(...matches);
                }
            }

            if (result == null) {
                result = searchForMatches(
                    '',
                    displayValues,
                    currentValueType,
                    props.keySelect,
                    props.getDisplayValue,
                    props.sortByDisplayValue,
                    false
                );
            }

            return result;
        },
        [currentValueType, props.keySelect, props.getDisplayValue, props.sortByDisplayValue, props.theme]
    );

    const processOnClose = React.useCallback(
        (value: IDisplayValue[]) => {
            dropGetValuesDef();

            if (props.onClose != null) {
                props.onClose(value);
            }
        },
        [props.onClose]
    );

    const processValueChange = React.useCallback(
        (value: ISuggestionValue[], close: boolean) => {
            setValues(value);

            if (props.onChange != null) {
                props.onChange(value);
            }

            if (close || props.singleSelectMode) {
                processOnClose(value);
            }
        },
        [props.onChange, props.singleSelectMode, processOnClose]
    );

    const moveFocus = React.useCallback(
        (moveUp: boolean) => {
            if (moveUp && searchFieldIsFocused.current) {
                return;
            }

            const matches = [...shownItems.current];

            if (clearSelectionsRef.current != null) {
                matches.unshift({
                    value: {
                        value: CLEAR_SELECTIONS_VALUE
                    }
                });
            }

            if (allItemRef.current != null) {
                matches.unshift({
                    value: {
                        value: ALL_ITEM_VALUE
                    }
                });
            }

            if (showMoreRef.current != null) {
                matches.push({
                    value: {
                        value: SHOW_MORE_VALUE
                    }
                });
            }

            const oldFocusedValue = focusedItem?.value;
            const index =
                oldFocusedValue != null ? matches.findIndex((match) => match.value.value === oldFocusedValue) : -1;

            let newFocusedItem: ISuggestionValue;

            if (index === -1) {
                newFocusedItem = matches[0]?.value;
            } else {
                if (moveUp) {
                    if (index === 0 && searchFieldRef.current != null) {
                        newFocusedItem = null;
                        searchFieldIsFocused.current = true;
                    } else {
                        newFocusedItem = getPreviousMatch(matches, index);
                    }
                } else {
                    newFocusedItem = getNextMatch(matches, index);
                }
            }

            if (newFocusedItem == null || newFocusedItem.value !== oldFocusedValue) {
                setFocusedItem(newFocusedItem);
            }
        },
        [focusedItem]
    );

    const processOnEnterPress = () => {
        if (!searchFieldIsFocused.current && focusedItem != null) {
            processItemClick(focusedItem, props.singleSelectMode);
        } else if (searchString.length > 0 && shownItems.current != null) {
            if (props.singleSelectMode) {
                if (focusedItem != null) {
                    processValueChange([{ ...focusedItem }], true);
                } else if (shownItems.current.length > 0) {
                    processValueChange([shownItems.current[0].value], true);
                } else {
                    processOnClose(values);
                }
            } else {
                const newValues = values != null ? [...values] : [];

                shownItems.current.forEach((item) => {
                    const valueIndex = newValues.findIndex((value) => {
                        return areSuggestionValuesEqual(value, item.value);
                    });

                    if (valueIndex < 0) {
                        newValues.push({ ...item.value });
                    }
                });

                processValueChange(newValues, false);
                searchFieldRef.current.value = '';
                searchStringIsChanged.current = true;
                setSearchString('');
            }
        } else {
            processOnClose(values);
        }
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.code) {
            case 'ArrowUp':
                e.preventDefault();
                moveFocus(true);
                break;

            case 'ArrowDown':
                e.preventDefault();
                moveFocus(false);
                break;

            case 'Enter':
                e.stopPropagation();
                e.preventDefault();
                processOnEnterPress();
                break;

            case 'Tab':
            case 'Escape':
                const newValue = normalizeValue(props.value);

                setValues(newValue);
                processOnClose(newValue);
                break;

            default:
                if (!searchFieldIsFocused.current) {
                    e.stopPropagation();
                    e.preventDefault();

                    if (searchString === '' && e.code === 'Space') {
                        makeSearchFieldFocused();
                        return;
                    }

                    const searchField = searchFieldRef.current;

                    if (searchField != null) {
                        let newValue: string;

                        if (e.code === 'Backspace') {
                            newValue = searchField.value = searchField.value.slice(0, -1);
                        } else if (e.key != null && e.key.length === 1) {
                            newValue = searchField.value += e.key;
                        } else {
                            return;
                        }

                        handleOnSearchFieldChange(newValue);
                    }
                }
        }
    };

    const processAllClick = React.useCallback(
        (searchString: string) => {
            const matches = getMatches(searchString);

            let newValues = values;

            if (newValues != null) {
                newValues = [...values];
                matches?.forEach((match) => {
                    const matchedValue = match.value;
                    const valueIndex = newValues.findIndex((value) => {
                        return areSuggestionValuesEqual(value, matchedValue);
                    });

                    if (valueIndex < 0) {
                        newValues.push(matchedValue);
                    }
                });
            } else {
                newValues = [];

                matches?.forEach((match) => {
                    newValues.push(match.value);
                });
            }

            processValueChange(newValues, false);
        },
        [getMatches, values, processValueChange]
    );

    const processShowMoreClick = React.useCallback(() => {
        setListThreshold(listThreshold + (props.listThreshold ?? SUGGESTIONS_LIST_THRESHOLD));
    }, [listThreshold, props.listThreshold]);

    const processItemClick = (value: ISuggestionValue, close: boolean) => {
        if (value.value === ALL_ITEM_VALUE) {
            processAllClick(searchString);
            return;
        }

        if (value.value === SHOW_MORE_VALUE) {
            processShowMoreClick();
            return;
        }

        let newValues: ISuggestionValue[];

        if (value.value === CLEAR_SELECTIONS_VALUE) {
            if (values == null || (values.length < 2 && props.allowNull === false)) {
                return;
            }

            const matches = getMatches(searchString);

            newValues = [];

            if (matches.length > 0) {
                value = matches[0].value;

                if (values.length > 0 && !props.singleSelectMode) {
                    // Clear the filtered values only.
                    values.forEach((value) => {
                        if (matches.findIndex((match) => areSuggestionValuesEqual(match.value, value)) < 0) {
                            newValues.push(value);
                        }
                    });
                }

                if (props.allowNull === false && newValues.length === 0) {
                    if (values.length > 0) {
                        newValues.push(values[0]);
                    } else {
                        newValues.push(value);
                    }
                }
            }
        } else if (props.singleSelectMode) {
            // Replace value.
            newValues = [{ ...value }];
        } else {
            // Merge values.
            newValues = values != null ? [...values] : [];

            const valueIndex = newValues.findIndex((newValue) => {
                return areSuggestionValuesEqual(newValue, value);
            });

            if (valueIndex > -1) {
                newValues.splice(valueIndex, 1);
            } else {
                newValues.push(value);
            }
        }

        if (props.allowNull !== false || newValues.length > 0) {
            processValueChange(newValues, close);
        }

        setFocusedItem(value);
    };

    const handleOnItemElementChange = (value: ISuggestionValue) => {
        processItemClick(value, false);
    };

    const handleOnSearchFieldChange = React.useCallback((value: string) => {
        searchStringIsChanged.current = true;
        setSearchString(value);
    }, []);

    const makeSearchFieldFocused = React.useCallback(() => {
        searchFieldIsFocused.current = true;
        setFocusedItem(null);
    }, []);

    const handleOnSearchFieldBlur = React.useCallback((e) => {
        searchFieldIsFocused.current = false;
    }, []);

    const validValuesExist = React.useCallback(() => {
        return getValuesResult.current.validValues != null;
    }, []);

    const isValueValid = React.useCallback((value: ISuggestionValue) => {
        if (validValuesExist()) {
            return (
                getValuesResult.current.validValues?.find((validValue) => {
                    return areSuggestionValuesEqual(value, validValue);
                }) != null
            );
        } else {
            return false;
        }
    }, []);

    const getSuggestions = () => {
        const { singleSelectMode, theme } = props;
        const matches = getMatches(searchString);
        const numberOfFoundItems = matches.length;

        shownItems.current = listThreshold > 0 ? matches.slice(0, listThreshold) : matches;

        let suggestions: React.JSX.Element[];

        if (numberOfFoundItems === 0) {
            const handleOnAdd = () => {
                if (searchString === '') {
                    return;
                }

                processValueChange(
                    [
                        {
                            value: searchString
                        }
                    ],
                    true
                );
            };

            suggestions = [<NoItemListItem key={'no-item'} allowAdding={props.allowAdding} onAdd={handleOnAdd} />];

            if (focusedItem != null) {
                setFocusedItem(null);
            }
        } else {
            const searchHighlightingColor = getSearchHighlightingColor(props.theme);

            let focusedItemIsFound = false;

            if (
                focusedItem != null &&
                (focusedItem.value === ALL_ITEM_VALUE ||
                    focusedItem.value === CLEAR_SELECTIONS_VALUE ||
                    focusedItem.value === SHOW_MORE_VALUE)
            ) {
                focusedItemIsFound = true;
            }

            let tabIndex = 1;

            suggestions = shownItems.current.map((obj, index) => {
                const { value, match } = obj;
                const itemKey = getItemKey(value);
                const isChecked = isItemChecked(value, values);

                if (!searchStringIsChanged.current) {
                    let itemIsFocused = focusedItem != null && focusedItem.value === value.value;

                    if (!focusedItemIsFound && itemIsFocused) {
                        focusedItemIsFound = true;
                    }
                }

                let validState: ValidState;

                if (validValuesExist()) {
                    validState = isValueValid(value) ? 'valid' : 'invalid';
                } else {
                    validState = 'none';
                }

                return (
                    <ListSelectItem
                        key={itemKey + '_' + index}
                        reference={(input) => {
                            itemsRef.current[itemKey] = input;
                        }}
                        value={value}
                        tabIndex={++tabIndex}
                        checked={isChecked}
                        singleSelectMode={singleSelectMode}
                        validState={validState}
                        title={<TitleOfMatch match={match} highlightingColor={searchHighlightingColor} />}
                        readOnly={props.readOnly}
                        theme={theme}
                        getIcon={props.getIcon}
                        onChange={handleOnItemElementChange}
                    />
                );
            });

            if (!props.singleSelectMode) {
                let numberOfValuesToClear = values != null ? values.length : 0;

                if (numberOfValuesToClear > 0) {
                    if (numberOfFoundItems > 0 && searchString !== '') {
                        values.forEach((value) => {
                            if (matches.findIndex((match) => areSuggestionValuesEqual(match.value, value)) < 0) {
                                if (props.allowNull !== false || numberOfValuesToClear > 1) {
                                    numberOfValuesToClear--;
                                }
                            }
                        });
                    } else if (props.allowNull === false && numberOfValuesToClear > 0) {
                        numberOfValuesToClear--;
                    }
                }

                if (!props.hideSelectAllAndClear) {
                    suggestions.unshift(
                        <ClearSelectionsItem
                            key="clear-selections"
                            reference={clearSelectionsRef}
                            tabIndex={1}
                            numberOfValuesToClear={numberOfValuesToClear}
                            theme={theme}
                            onChange={handleOnItemElementChange}
                        />
                    );

                    if (numberOfFoundItems > 1) {
                        suggestions.unshift(
                            <SelectAllItem
                                key="select-all"
                                reference={allItemRef}
                                tabIndex={0}
                                numberOfFoundItems={numberOfFoundItems}
                                theme={theme}
                                onChange={handleOnItemElementChange}
                            />
                        );
                    }
                }
            }

            if (numberOfFoundItems > listThreshold) {
                suggestions.push(
                    <ShowMoreListItem
                        key="show-more"
                        reference={showMoreRef}
                        tabIndex={++tabIndex}
                        numberOfHiddenItems={numberOfFoundItems - listThreshold}
                        onClick={() => {
                            setFocusedItem({ value: SHOW_MORE_VALUE });
                            processShowMoreClick();
                        }}
                    />
                );
            }

            if (!focusedItemIsFound && !searchFieldIsFocused.current) {
                searchStringIsChanged.current = false;
                setFocusedItem(shownItems.current[0].value);
            }
        }

        return suggestions;
    };

    let searchField: React.JSX.Element;

    if (!props.hideSearchField) {
        searchField = (
            <SearchField
                reference={searchFieldRef}
                defaultValue={props.defaultSearchString}
                onFocus={makeSearchFieldFocused}
                onBlur={handleOnSearchFieldBlur}
                onChange={handleOnSearchFieldChange}
            />
        );
    }

    let typesSelector: React.JSX.Element;

    if (props.types != null) {
        typesSelector = (
            <TypesSelector
                types={props.types}
                currentValueType={currentValueType}
                onSelect={(key: string) => {
                    setCurrentValueType(key);
                }}
            />
        );
    }

    let content: React.JSX.Element | React.JSX.Element[];

    if (loading.current) {
        content = (
            <li>
                <ProgressBar />
            </li>
        );
    } else {
        content = getSuggestions();
    }

    const containerStyle: React.CSSProperties = {};
    const width = getCSSWidth(props.width);
    const minWidth = getCSSWidth(props.minWidth);
    const maxHeight = getCSSWidth(props.maxHeight);

    if (width != null) {
        containerStyle.width = width;
    }

    if (minWidth != null) {
        containerStyle.minWidth = minWidth;
    }

    if (maxHeight != null) {
        containerStyle.maxHeight = maxHeight;
        containerStyle.overflow = 'auto';
    }

    if (props.popupMode) {
        containerStyle.borderStyle = 'none';
    }

    if (props.theme != null) {
        const background = props.theme[themeVars.itemSelector_ItemListBackgroundColor];

        if (background != null) {
            containerStyle.background = background;
        }
    }

    return (
        <div
            className="suggestions-list"
            style={containerStyle}
            onClick={props.onContainerClick}
            onKeyDown={handleOnKeyDown}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
        >
            {searchField}
            {typesSelector}

            <ul className="item-list list-group" ref={containerRef} tabIndex={0}>
                {content}
            </ul>
        </div>
    );
}

function ClearSelectionsItem(props: {
    reference: React.Ref<HTMLLIElement>;
    tabIndex: number;
    numberOfValuesToClear: number;
    theme: IValuesCollection;
    onChange(value: ISuggestionValue): void;
}) {
    const title = Globalization.getMessage('clearSelections');

    return (
        <ListSelectItem
            reference={props.reference}
            value={{
                value: CLEAR_SELECTIONS_VALUE,
                displayValue: title
            }}
            tabIndex={props.tabIndex}
            checked={null}
            singleSelectMode={false}
            title={<strong>{title + ` (${props.numberOfValuesToClear})`}</strong>}
            validState="none"
            theme={props.theme}
            onChange={props.onChange}
        />
    );
}

function SelectAllItem(props: {
    reference: React.Ref<HTMLLIElement>;
    tabIndex: number;
    numberOfFoundItems: number;
    theme: IValuesCollection;
    onChange(value: ISuggestionValue): void;
}) {
    const title = Globalization.getMessage('selectAll');

    return (
        <ListSelectItem
            reference={props.reference}
            value={{
                value: ALL_ITEM_VALUE,
                displayValue: title
            }}
            tabIndex={props.tabIndex}
            checked={null}
            singleSelectMode={false}
            title={<strong>{title + ` (${props.numberOfFoundItems})`}</strong>}
            validState="none"
            theme={props.theme}
            onChange={props.onChange}
        />
    );
}

function NoItemListItem(props: { allowAdding: boolean; onAdd: React.MouseEventHandler<HTMLButtonElement> }) {
    let addNewButton: React.JSX.Element;

    if (props.allowAdding) {
        addNewButton = (
            <button className="btn btn-default ml-1" onClick={props.onAdd}>
                {'Add New'}
            </button>
        );
    }

    return (
        <li>
            <div className="node-item-element">
                <NoItemElement />
                {addNewButton}
            </div>
        </li>
    );
}

function TypesSelector(props: { types: IType[]; currentValueType: string; onSelect(key: string): void }) {
    const getOnTypeClickHandler = React.useCallback((key: string) => {
        return (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
            props.onSelect(key);
        };
    }, []);

    const typeElements = props.types.map((obj) => {
        return (
            <div
                key={obj.key}
                className={'btn btn-default btn-sm' + (obj.key === props.currentValueType ? ' active' : '')}
                onClick={getOnTypeClickHandler(obj.key)}
            >
                {obj.title}
            </div>
        );
    });

    return (
        <div className="types-container">
            <div className="btn-group">{typeElements}</div>
        </div>
    );
}

function ShowMoreListItem(props: {
    reference: React.Ref<HTMLLIElement>;
    tabIndex: number;
    numberOfHiddenItems: number;
    onClick(e: React.MouseEvent<HTMLDivElement>): void;
}) {
    return (
        <li className="show-more" ref={props.reference} tabIndex={props.tabIndex}>
            <ShowMoreElement
                className="node-item-element"
                numberOfHiddenItems={props.numberOfHiddenItems}
                onClick={props.onClick}
            />
        </li>
    );
}
